import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ChevronDown, ChevronsLeft, ChevronsRight, ChevronUp } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import logoMark from '../../img/logo-branco.png';
import { useNavSections } from './navConfig';
import { mobileSidebarOpenState, sidebarExpandedSelector } from './sidebarState';

const MOBILE_BREAKPOINT = '(max-width:1023.95px)';

// Seções que ganham um chevron pra recolher/expandir a lista, já recolhidas
// por padrão — evita empurrar as seções seguintes pra fora da viewport em
// telas menores. Fixo por id (não por contagem de itens): os itens de cada
// seção são 100% gated por permissão (navConfig.js), então no primeiro
// render — antes do /me resolver — o array de itens vem vazio e uma regra
// baseada em `items.length` erraria o default nesse instante.
const COLLAPSIBLE_SECTION_IDS = new Set(['financeiro', 'cadastros', 'sistema', 'admin', 'relatorios']);

/**
 * App shell sidebar — DESIGN-SYSTEM.md §3.1.
 *
 * Fixed width 264px, collapsible to 76px (icon-only). Always uses its own
 * dark palette (`theme.customSidebar`), independent from `palette.mode`.
 * Below 1024px (celular e tablet) it becomes an off-canvas drawer with an overlay.
 *
 * Grouping/permission logic lives in `navConfig.js` — this component only
 * renders it, it does not decide what the user can see.
 */
const ModernSidebar = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
    const [expanded, setExpanded] = useRecoilState(sidebarExpandedSelector);
    const [mobileOpen, setMobileOpen] = useRecoilState(mobileSidebarOpenState);
    const sections = useNavSections();
    // Seções em COLLAPSIBLE_SECTION_IDS sempre carregam recolhidas — sem
    // persistência (nem localStorage nem recoil): cada load/remount começa
    // do zero, só a interação da sessão atual (clicar no chevron) muda isso.
    const [collapsedSections, setCollapsedSections] = useState(() => {
        const initial = {};
        COLLAPSIBLE_SECTION_IDS.forEach((id) => { initial[id] = true; });
        return initial;
    });
    // Legenda (tooltip) dos itens do menu quando a sidebar está recolhida —
    // ver DESIGN-SYSTEM.md §3.1 extensão "Sidebar collapsed nav tooltip".
    // Único elemento, montado sempre (opacity/visibility controlam o show),
    // e renderizado via portal em document.body: o container de nav abaixo
    // tem overflowY:auto, o que faz overflowX computar como "auto" também
    // (spec CSS), clipando qualquer tooltip filho que vaze da faixa da
    // sidebar. Posição calculada via getBoundingClientRect() do item.
    const [navTooltip, setNavTooltip] = useState({ visible: false, label: '', top: 0, left: 0 });
    const navScrollRef = useRef(null);

    const sidebar = theme.customSidebar;
    // Estado base (preferência persistida) vs. expansão temporária por hover:
    // `hovered` é local, nunca grava em sidebarExpandedSelector.
    const [hovered, setHovered] = useState(false);
    const baseCollapsed = !isMobile && !expanded;
    const hoverExpanded = baseCollapsed && hovered;
    const collapsed = baseCollapsed && !hovered;
    const sidebarWidth = collapsed ? 76 : 264;
    // Só faz sentido recolher uma seção quando o label dela está visível —
    // no modo ícone (sidebar recolhida no desktop) não há onde colocar o
    // chevron, então mostramos todos os itens direto.
    const showSectionHeaders = !collapsed || isMobile;

    // O hub /dashboards ativa o item em qualquer aba (/dashboards/sale, ...).
    const isActive = (path) => location.pathname === path || (path === '/dashboards' && location.pathname.startsWith('/dashboards/'));

    const hideNavTooltip = useCallback(() => {
        setNavTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    }, []);

    const showNavTooltip = (event, label) => {
        if (!collapsed || isMobile) return;
        const rect = event.currentTarget.getBoundingClientRect();
        setNavTooltip({ visible: true, label, top: rect.top + rect.height / 2, left: rect.right + 14 });
    };

    // Esconde o tooltip sempre que a sidebar alterna recolhida/expandida (ou
    // vira drawer mobile) — a posição calculada no hover anterior fica
    // obsoleta assim que o layout muda.
    useEffect(() => {
        hideNavTooltip();
    }, [collapsed, isMobile, hideNavTooltip]);

    useEffect(() => {
        const node = navScrollRef.current;
        if (!node) return undefined;
        node.addEventListener('scroll', hideNavTooltip, { passive: true });
        window.addEventListener('resize', hideNavTooltip);
        return () => {
            node.removeEventListener('scroll', hideNavTooltip);
            window.removeEventListener('resize', hideNavTooltip);
        };
    }, [hideNavTooltip]);

    const toggleSection = (sectionId) => {
        setCollapsedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
    };

    const handleNavigate = (path) => {
        navigate(path);
        if (isMobile) setMobileOpen(false);
    };

    const handleItemClick = (item) => {
        handleNavigate(item.path);
        // Clique num item recolhe de volta o menu expandido por hover; só
        // reabre após o mouse sair e entrar de novo (onMouseEnter do aside).
        setHovered(false);
    };

    return (
        <>
            {isMobile && mobileOpen && (
                <Box
                    onClick={() => setMobileOpen(false)}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(8,12,24,.5)',
                        zIndex: theme.zIndex.drawer,
                    }}
                />
            )}
            {/* Reserva o espaço do menu no fluxo, já que o menu é fixed */}
            {!isMobile && (
                <Box aria-hidden sx={{ width: baseCollapsed ? 76 : 264, flexShrink: 0, transition: 'width .2s ease' }} />
            )}
            <Box
                component="aside"
                onMouseEnter={baseCollapsed ? () => setHovered(true) : undefined}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    width: sidebarWidth,
                    flexShrink: 0,
                    backgroundColor: sidebar.bg,
                    color: sidebar.text,
                    borderRight: `1px solid ${sidebar.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'width .2s ease, transform .25s ease',
                    // fixed (não sticky): html/body têm overflow-x hidden no tema, o que
                    // quebra o sticky e fazia o menu rolar junto com a página
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100vh',
                    // dvh: no tablet/celular a barra do navegador não corta o fim do menu
                    '@supports (height: 100dvh)': { height: '100dvh' },
                    zIndex: theme.zIndex.drawer + 1,
                    ...(hoverExpanded && { boxShadow: theme.customShadows.lg }),
                    ...(isMobile && {
                        width: 264,
                        boxShadow: theme.customShadows.lg,
                        transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
                    }),
                }}
            >
                {/* Logo */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: collapsed && !isMobile ? 1 : 2.25,
                        py: 2.5,
                        borderBottom: `1px solid ${sidebar.border}`,
                        justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                    }}
                >
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '10px',
                            flexShrink: 0,
                            backgroundImage: theme.palette.gradient.brand,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 14px rgba(37,99,235,.4)',
                        }}
                    >
                        {/* Marca oficial do FlowVendas (mesma composição do
                            `.logo-badge` de `landing-flow-vendas.html`: selo
                            34x34 com o gradiente da marca + logo branco de
                            ~20px dentro) — substitui o placeholder "FV". */}
                        <Box
                            component="img"
                            src={logoMark}
                            alt="FlowVendas"
                            sx={{ width: 20, height: 20, display: 'block', objectFit: 'contain' }}
                        />
                    </Box>
                    {(!collapsed || isMobile) && (
                        <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#fff', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                            Flow<Box component="span" sx={{ color: theme.palette.brand2.main }}>Vendas</Box>
                        </Typography>
                    )}
                </Box>

                {/* Nav */}
                <Box ref={navScrollRef} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 1.5, py: 1 }}>
                    {sections.map((section) => {
                        const canCollapse = COLLAPSIBLE_SECTION_IDS.has(section.id);
                        const sectionCollapsed = showSectionHeaders && canCollapse && !!collapsedSections[section.id];
                        const visibleItems = sectionCollapsed ? [] : section.items;
                        return (
                            <Box key={section.id} sx={{ mt: 2.25, '&:first-of-type': { mt: 0.5 } }}>
                            {showSectionHeaders && (
                                <Box
                                    onClick={canCollapse ? () => toggleSection(section.id) : undefined}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 0.5,
                                        px: 1.25,
                                        pb: 1,
                                        cursor: canCollapse ? 'pointer' : 'default',
                                        userSelect: 'none',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '10.5px',
                                            fontWeight: 700,
                                            letterSpacing: '.08em',
                                            textTransform: 'uppercase',
                                            color: sidebar.textDim,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {section.label}
                                    </Typography>
                                    {canCollapse && (
                                        sectionCollapsed
                                            ? <ChevronDown size={13} strokeWidth={2.5} style={{ color: sidebar.textDim, flexShrink: 0 }} />
                                            : <ChevronUp size={13} strokeWidth={2.5} style={{ color: sidebar.textDim, flexShrink: 0 }} />
                                    )}
                                </Box>
                            )}
                            {visibleItems.map((item) => {
                                const active = isActive(item.path);
                                const Icon = item.icon;
                                return (
                                    <Box
                                        key={item.id}
                                        onClick={() => handleItemClick(item)}
                                        onMouseEnter={(event) => showNavTooltip(event, item.label)}
                                        onMouseLeave={hideNavTooltip}
                                        aria-label={collapsed && !isMobile ? item.label : undefined}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            py: 1.1,
                                            px: 1.25,
                                            mb: 0.25,
                                            borderRadius: '10px',
                                            fontSize: '13.5px',
                                            fontWeight: 500,
                                            color: active ? '#fff' : sidebar.text,
                                            cursor: 'pointer',
                                            position: 'relative',
                                            border: `1px solid ${active ? sidebar.border : 'transparent'}`,
                                            backgroundColor: active ? sidebar.active : 'transparent',
                                            justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                                            whiteSpace: 'nowrap',
                                            '&:hover': {
                                                backgroundColor: sidebar.active,
                                                color: '#fff',
                                            },
                                            '&::before': active ? {
                                                content: '""',
                                                position: 'absolute',
                                                left: -12,
                                                top: 8,
                                                bottom: 8,
                                                width: 3,
                                                borderRadius: 3,
                                                backgroundImage: theme.palette.gradient.brand,
                                            } : undefined,
                                        }}
                                    >
                                        <Icon size={18} strokeWidth={2} style={{ opacity: 0.85, flexShrink: 0 }} />
                                        {(!collapsed || isMobile) && <span>{item.label}</span>}
                                        {item.badge && (!collapsed || isMobile) && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    ml: 'auto',
                                                    fontSize: '10.5px',
                                                    fontWeight: 700,
                                                    color: '#fff',
                                                    px: '7px',
                                                    py: '1px',
                                                    borderRadius: '20px',
                                                    backgroundImage: theme.palette.gradient.brand,
                                                }}
                                            >
                                                {item.badge}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                        );
                    })}
                </Box>

                {/* Footer / collapse toggle (desktop only) */}
                {!isMobile && (
                    <Box sx={{ p: 1.5, borderTop: `1px solid ${sidebar.border}` }}>
                        <Box
                            component="button"
                            onClick={() => setExpanded(!expanded)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                gap: 1.25,
                                width: '100%',
                                py: 1.1,
                                px: 1.25,
                                borderRadius: '10px',
                                backgroundColor: 'transparent',
                                border: `1px solid ${sidebar.border}`,
                                color: sidebar.textDim,
                                fontSize: '12.5px',
                                fontFamily: 'inherit',
                                cursor: 'pointer',
                                '&:hover': { color: '#fff', borderColor: theme.palette.primary.main },
                            }}
                        >
                            {baseCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
                            {!collapsed && <span>{baseCollapsed ? 'Fixar menu' : 'Recolher menu'}</span>}
                        </Box>
                    </Box>
                )}
            </Box>
            {createPortal(
                <Box
                    role="tooltip"
                    aria-hidden={!navTooltip.visible}
                    sx={{
                        position: 'fixed',
                        zIndex: theme.zIndex.tooltip,
                        top: navTooltip.top,
                        left: navTooltip.left,
                        backgroundColor: 'text.primary',
                        color: 'background.default',
                        fontSize: '12px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        px: 1.25,
                        py: 0.75,
                        borderRadius: `${theme.customRadius.control}px`,
                        boxShadow: theme.customShadows.md,
                        opacity: navTooltip.visible ? 1 : 0,
                        visibility: navTooltip.visible ? 'visible' : 'hidden',
                        pointerEvents: 'none',
                        transition: 'opacity .15s ease, transform .15s ease',
                        transform: `translateY(-50%) translateX(${navTooltip.visible ? 0 : -4}px)`,
                        ...(theme.palette.mode === 'dark' && {
                            backgroundColor: 'background.paper',
                            color: 'text.primary',
                            border: `1px solid ${theme.palette.divider}`,
                        }),
                    }}
                >
                    {navTooltip.label}
                </Box>,
                document.body,
            )}
        </>
    );
};

export default ModernSidebar;
